/* global Blockly */
import React from "react";

import util from "../../../../../../utils/es5Utils";

export const WIDGET_ACTIONS = {
  barcode: [["Scan Barcode", "Camera"]],
  businesscard: [
    ["Launch Camera", "Camera"],
    ["Upload Picture", "Action"],
  ],
  button: [["Press button", "Action"]],
  camera: [
    ["Launch Camera", "Camera"],
    ["Upload Picture", "Action"],
  ],
  flipswitch: [
    ["Set to ON", "SwitchOn"],
    ["Set to OFF", "SwitchOff"],
    ["Toggle switch", "SwitchToggle"],
  ],
  geolocation: [["Get location", "Action"]],
  nfc: [["Open NFC", "Focus"]],
  qrcode: [["Generate QR code", "Action"]],
  signature: [["Open signature", "Focus"]],
  textbox: [
    ["Launch barcode camera", "Camera"],
    ["Launch keyboard", "Keyboard"],
  ],
  textscanner: [["Launch scanner", "Action"]],
  timer: [
    ["Start timer", "StartTimer"],
    ["Stop timer", "StopTimer"],
    ["Reset timer", "ResetTimer"],
  ],
};

export function isEligibleWidget(widget) {
  if (!widget || !Object.hasOwn(WIDGET_ACTIONS, widget.type)) {
    return false;
  }
  if (widget.type === "textbox") {
    return Boolean(widget.properties?.useAsBarcode);
  }
  return true;
}

export function getEligibleWidgets(context) {
  return util.widgetsOfType(context).filter(isEligibleWidget);
}

function findEligibleWidget(context, widgetId) {
  return getEligibleWidgets(context).find((widget) => widget.id === widgetId);
}

function buildActionOptions(widgetType) {
  return WIDGET_ACTIONS[widgetType]?.length > 0
    ? WIDGET_ACTIONS[widgetType]
    : [["- no actions -", ""]];
}

function defineBlock(context) {
  return {
    widgetType: "",
    pageId: "",

    init() {
      const selectWidgetDropdown = new Blockly.FieldWidgetsDropdown(
        context,
        isEligibleWidget,
        undefined,
        "- select -",
        this.onWidgetChanged_.bind(this),
      );

      this.appendDummyInput("WIDGET").appendField("launch").appendField(selectWidgetDropdown, "ID");

      this.rebuildActionField_(this.widgetType);

      this.appendStatementInput("THEN").appendField("then");

      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(Blockly.Msg.APP_ACTIONS_HUE);
      this.setTooltip(
        "Launch a widget-specific action.",
      );
      this.setHelpUrl(`${Blockly.BASE_HELP_URL}#app-actions`);
    },

    onWidgetChanged_(widgetId) {
      const widget = findEligibleWidget(context, widgetId);
      if (widget) {
        this.pageId = widget.page.id;
      }

      const newType = widget ? widget.type : "";
      if (newType !== this.widgetType) {
        this.widgetType = newType;
        this.rebuildActionField_(newType);
      }
    },

    rebuildActionField_(widgetType) {
      if (this.getInput("ACTION_INPUT")) {
        this.removeInput("ACTION_INPUT");
      }

      const actionDropdown = new Blockly.FieldDropdown(buildActionOptions(widgetType));
      this.appendDummyInput("ACTION_INPUT")
        .appendField("widget action")
        .appendField(actionDropdown, "ACTION");

      if (this.getInput("THEN")) {
        this.moveInputBefore("ACTION_INPUT", "THEN");
      }
    },
  };
}

function defineGenerators() {
  return {
    JavaScript(block) {
      const innerBlockCode = Blockly.JavaScript.statementToCode(block, "THEN");
      const code = `
            actionsModule.executeActionWithCallback({
                action: {
                    actionType: "launchWidgetAction",
                    parameters: {
                        navigateTo: "${this.pageId}",
                        navigateToWidget: "${block.getFieldValue("ID")}",
                        setFocusActionType: "${block.getFieldValue("ACTION")}"
                    }
                }
            }, function() {
                ${innerBlockCode}
            });\n`;
      return code;
    },
  };
}

const LaunchWidgetAction = (props) => {
  const name = "snap_action_launch_widget_action_V3";
  LaunchWidgetAction.blockName = name;

  Blockly.Blocks[name] = defineBlock(props.context);
  const generators = defineGenerators();
  for (const i in generators) {
    Blockly[i][name] = generators[i];
  }

  return getEligibleWidgets(props.context).length > 0 ? <block type={name}></block> : null;
};

export default LaunchWidgetAction;
